import { useState } from "react";
import { Box, Button, IconButton, Tooltip, Drawer, List, ListItemButton, ListItemText } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import whiteLogo from "../assets/white-logo.svg";
import { useAuthGate } from "../hooks/useAuthGate";
import { useThemeContext } from "../utils/ThemeContext";
import { usePanelTokens } from "../utils/panelTokens";
import { DOWNLOADS_PUBLIC_PATH } from "./components/downloads/catalog";
import { goToLogin } from "../lib/accounts";
import { ROUTES } from "../routes";
import { Link as RouterLink } from "react-router-dom";

export const PUBLIC_ACCENT = "#3a8eff";

// The public developer homepage — where the logo and "Home" always land.
export const DEVELOPER_HOME = ROUTES.publicHome;

// Main nav (NVIDIA-style top row). `soon` renders a non-navigating "Soon" pill —
// swap in real URLs when the blog/forum exist.
const NAV_MAIN = [
  { label: "Home", href: DEVELOPER_HOME },
  { label: "Docs", href: "https://docs.vecros.com/", external: true },
  { label: "Downloads", href: DOWNLOADS_PUBLIC_PATH },
  { label: "Training", href: ROUTES.training },
  { label: "Forum", soon: true },
  { label: "Blog", soon: true },
];

/**
 * Sign-in that tags the return trip so the app registers the visitor as a
 * developer on arrival (see Home2). Plain `requireSignin` is the untagged path.
 */
export const joinAsDeveloper = () =>
  goToLogin(`${window.location.origin}${ROUTES.developer}?dev_join=1`);

/**
 * The website header every logged-out page wears — landing and the public tool
 * pages alike, so the site nav doesn't vanish the moment someone opens a tool.
 * `children` renders as a secondary row underneath (the landing's anchor links).
 */
const PublicHeader = ({
  accent = PUBLIC_ACCENT,
  appPath = ROUTES.developer,
  sticky = false,
  children,
}) => {
  const t = usePanelTokens();
  const { isAuthenticated, requireSignin } = useAuthGate();
  const { themeMode, toggleTheme } = useThemeContext();
  const [navOpen, setNavOpen] = useState(false);

  return (
    <Box
      component="header"
      sx={{
        flexShrink: 0,
        ...(sticky ? { position: "sticky", top: 0, zIndex: 10 } : null),
        background: t.ground,
        borderBottom: `1px solid ${t.border}`,
      }}
    >
      <Box sx={{ height: 56, px: { xs: 2.5, md: 4 }, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, minWidth: 0 }}>
          <Box
            component="a"
            href={DEVELOPER_HOME}
            sx={{ display: "flex", flexShrink: 0 }}
          >
            <Box
              component="img"
              src={whiteLogo}
              alt="Vecros"
              sx={{ height: 22, filter: t.ground === "#000" ? "none" : "invert(1)" }}
            />
          </Box>
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2.5, fontSize: 13.5, fontWeight: 600 }}>
            {NAV_MAIN.map((l) =>
              l.soon ? (
                <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.6, color: t.textMuted, cursor: "default" }}>
                  {l.label}
                  <Box sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".06em", px: 0.6, py: 0.1, borderRadius: 1, border: `1px solid ${t.border}` }}>
                    SOON
                  </Box>
                </Box>
              ) : (
                <Box
                  key={l.label}
                  component="a"
                  href={l.href}
                  target={l.external ? "_blank" : undefined}
                  rel={l.external ? "noopener noreferrer" : undefined}
                  sx={{ color: t.text, textDecoration: "none", "&:hover": { color: accent } }}
                >
                  {l.label}
                </Box>
              )
            )}
          </Box>
          <IconButton
            aria-label="Open menu"
            onClick={() => setNavOpen(true)}
            size="small"
            sx={{ display: { xs: "inline-flex", md: "none" }, color: t.text }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Tooltip title={themeMode ? "Switch to light mode" : "Switch to dark mode"} arrow>
            <IconButton onClick={toggleTheme} size="small" sx={{ color: t.textMuted }}>
              {themeMode ? <LightModeOutlinedIcon sx={{ fontSize: 20 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          {isAuthenticated ? (
            <Button
              component={RouterLink}
              to={appPath}
              variant="contained"
              sx={{ backgroundColor: accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2, "&:hover": { backgroundColor: accent, filter: "brightness(0.95)" } }}
            >
              Access the app
            </Button>
          ) : (
            <>
              <Button onClick={() => requireSignin()} sx={{ color: t.text, textTransform: "none", fontWeight: 600 }}>
                Sign in
              </Button>
              <Button
                onClick={joinAsDeveloper}
                variant="contained"
                sx={{ backgroundColor: accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2, whiteSpace: "nowrap", "&:hover": { backgroundColor: accent, filter: "brightness(0.95)" } }}
              >
                Join as developer
              </Button>
            </>
          )}
        </Box>
      </Box>
      {children}

      <Drawer anchor="right" open={navOpen} onClose={() => setNavOpen(false)}>
        <List sx={{ width: 220, backgroundColor: t.ground, height: "100%", py: 1 }}>
          {NAV_MAIN.map((l) =>
            l.soon ? (
              <ListItemButton key={l.label} disabled sx={{ color: t.textMuted, gap: 0.6 }}>
                <ListItemText primary={l.label} />
                <Box sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".06em", px: 0.6, py: 0.1, borderRadius: 1, border: `1px solid ${t.border}` }}>
                  SOON
                </Box>
              </ListItemButton>
            ) : (
              <ListItemButton
                key={l.label}
                component="a"
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                onClick={() => setNavOpen(false)}
                sx={{ color: t.text }}
              >
                <ListItemText primary={l.label} />
              </ListItemButton>
            )
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default PublicHeader;
